// @ts-nocheck — full types will be added in Sprint 2
import AweSomeIcon from "../assets/images/moods/Awesome.png";
import GoodIcon from "../assets/images/moods/Good.png";
import NeutralIcon from "../assets/images/moods/Neutral.png";
import BadIcon from "../assets/images/moods/Bad.png";
import AwfulIcon from "../assets/images/moods/Awful.png";

export const ROLE = {
  SUPER_ADMIN: "SUPER_ADMIN",
  TENANT_ADMIN: "TENANT_ADMIN",
  TENANT_SUPER_ADMIN: "TENANT_SUPER_ADMIN",
  TENANT_USER: "TENANT_USER",
  UNKNOWN: "UNKNOWN",
} as const;

export type UserRole = (typeof ROLE)[keyof typeof ROLE];

export const SETTINGS_ROUTES = {
  PRACTICE: "practice",
  CONFIGURATION: "configuration",
  CONTENT: "content",
  RESCUE: "rescue",
} as const;

export const AGE_LABELS = {
  under18: "under 18",
  "13_17": "under 18",
  "18_24": "18-24",
  "25_34": "25-34",
  "35_44": "35-44",
  "45_54": "45-54",
  "55_64": "55-64",
  "65+": "65+",
  "18_29": "18-29",
  "30_39": "30-39",
  "40_49": "40-49",
  "50_plus": "50+",
  "50+": "50+",
  secret: "Prefer not to say",
};

export const GENDER_LABELS = {
  female: "Female",
  male: "Male",
  nonBinary: "Non-binary",
  other: "Other",
  secret: "Prefer not to say",
  preferNotToSay: "Prefer not to say",
};

export const DAILY_RHYTHM_LABELS = {
  earlyBird: "Early bird",
  nightOwl: "Night owl",
  balanced: "Balanced",
  flexible: "Flexible",
};

export const InteractivityTranslates = {
  journey: {
    en: "Journey",
    ru: "Путешествие",
    uk: "Подорож",
  },
  meditation: {
    en: "Meditation",
    ru: "Медитация",
    uk: "Медитація",
  },
  practice: {
    en: "Audio practice",
    ru: "Аудиопрактика",
    uk: "Аудіопрактика",
  },
  affirmation: {
    en: "Affirmation",
    ru: "Афирмация",
    uk: "Афірмація",
  },
  note: {
    en: "Practice",
    ru: "Практика",
    uk: "Практика",
  },
  instruction: {
    en: "Practice",
    ru: "Практика",
    uk: "Практика",
  },
  list: {
    en: "Practice",
    ru: "Практика",
    uk: "Практика",
  },
  test: {
    en: "Test",
    ru: "Тест",
    uk: "Тест",
  },
  undefined: {
    en: "Practice",
    ru: "Практика",
    uk: "Практика",
  },
};

export const CommonTranslates = {
  Add: {
    en: "Add",
    ru: "Добавить",
    uk: "Додати",
  },
  Update: {
    en: "Update",
    ru: "Обновить",
    uk: "Оновити",
  },
  Remove: {
    en: "Remove",
    ru: "Удалить",
    uk: "Видалити",
  },
};

export const ClientAnalyticsRangeOptions = [
  {
    label: "7 days",
    value: "d7",
  },
  {
    label: "30 days",
    value: "d30",
  },
  {
    label: "90 days",
    value: "d90",
  },
  {
    label: "1 year",
    value: "year",
  },
];

export const MoodTrendsOptions = [
  {
    label: "Mindful minutes",
    value: "mindful",
  },
  {
    label: "Time in the app",
    value: "app-time",
  },
  {
    label: "Mood check-ins",
    value: "mood",
  },
  {
    label: "AI assistant",
    value: "ai-assistant",
  },
];

export const moodRates = [
  { value: -2, key: "Awful", color: "166,164,229", icon: AwfulIcon },
  { value: -1, key: "Bad", color: "144,202,249 ", icon: BadIcon },
  { value: 0, key: "Neutral", color: "251,140,0", icon: NeutralIcon },
  { value: 1, key: "Good", color: "255,213,79", icon: GoodIcon },
  { value: 2, key: "Awesome", color: "179,208,130", icon: AweSomeIcon },
];

export const moodMap = {
  2: "Awesome",
  1: "Good",
  0: "Meh",
  "-1": "Bad",
  "-2": "Awful",
};

export const moodColors = {
  Awesome: "#B3D082",
  Good: "#FFD54F",
  Meh: "#FFB74D",
  Bad: "#90CAF9",
  Awful: "#A6A4E5",
};

export const ERROR_CODES = {
  EMAIL_ALREADY_EXISTS: "EMAIL_ALREADY_EXISTS",
  INVITATION_ALREADY_EXISTS: "INVITATION_ALREADY_EXISTS",
  SELF_INVITE_REQUIRES_SELF_ASSIGNMENT: "SELF_INVITE_REQUIRES_SELF_ASSIGNMENT",
} as const;

export const ERRORS = {
  INITIALIZE_ERROR: "Something went wrong during setup.",
  NO_ID_TOKEN: "Authentication failed. Please try again.",
  NO_EMAIL: "Email is required.",
  UNAVAILABLE: "Service is currently unavailable. Please try later.",
  UNAUTHORIZED: "You are not authorized to perform this action.",
  EMAIL_ALREADY_EXISTS: "User with this email already exists",
  INVITATION_ALREADY_EXISTS: "An invitation with this email already exists",
  SELF_INVITE_REQUIRES_SELF_ASSIGNMENT:
    "To invite yourself as a client, assign yourself as a therapist",
  WRONG_INVITATION: "Sorry, your invitation has expired",
  WRONG_TOKEN: "Invalid or expired token.",
  NOT_FOUND: "Record not found. Please refresh the page and try again.",
  IS_PRIMARY_THERAPIST: "You are the primary therapist. Ask an Account Admin to reassign before unassigning yourself.",
  ACCOUNT_DEACTIVATED: "This account has been deactivated. Please contact your administrator.",
};

function getTimezoneOffset(timeZone) {
  const now = new Date();
  const tzString = now.toLocaleString("en-US", { timeZone });
  const localString = now.toLocaleString("en-US");
  const diff = (Date.parse(localString) - Date.parse(tzString)) / 3600000;
  const offset = diff + now.getTimezoneOffset() / 60;

  return -offset;
}

export const getSystemTimezones = () => {
  const zones = Intl.supportedValuesOf("timeZone")
    .map((zone) => {
      const offset = getTimezoneOffset(zone);
      const timezone = `${zone} (UTC ${
        offset >= 0 ? "+" + offset : offset
      }:00)`;
      return { timezone, offset };
    })
    .sort((a, b) => a.offset - b.offset);

  return zones;
};

export const geSystemTimezoneOffset = () => {
  const offset = (new Date().getTimezoneOffset() / 60) * -1;
  return getSystemTimezones().find((timezone) => timezone.offset === offset)
    ?.timezone;
};

export const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const MoodTrendsColors = {
  mindful: "#b3d082",
  "app-time": "#90CAF9",
  mood: "#A6A4E5",
  "ai-assistant": "#ef66e6",
  "journey-sessions": "#ECF3E0",
  "audio-practices": "#F9E3E9",
  exercises: "#FFF7DC",
  "time-in-app": "#E3F4FE",
  "mood-check-ins": "#EEEEF8",
  "ai-assistant-bar": "#f8e6f6",
  moodLine: "#A6A4E5",
  moodGradient: {
    0: "#A6A4E5",
    0.25: "#90CAF9",
    0.5: "#FB8C00",
    0.75: "#FFD54F",
    1: "#B3D082",
  },
};

export const filterTypeButtons = [
  {
    group: {
      id: "journey",
      title: { en: "Journeys", ru: "Путешествия", uk: "Подорожі" },
    },
  },
  {
    group: {
      id: "meditation",
      title: { en: "Meditations", ru: "Медитации", uk: "Медитації" },
    },
  },
  {
    group: {
      id: "practice",
      title: {
        en: "Audio-practice",
        ru: "Аудио-практики",
        uk: "Аудіо-практики",
      },
    },
  },
  {
    group: {
      id: "questions",
      title: { en: "Practices", ru: "Практики", uk: "Практики" },
    },
  },
  {
    group: {
      id: "affirmation",
      title: { en: "Affirmations", ru: "Утверждения", uk: "Утвердження" },
    },
  },
  {
    group: {
      id: "test",
      title: { en: "Tests", ru: "Тесты", uk: "Тести" },
    },
  },
];

export const groupName = {
  Morning: "Morning Routine",
  Evening: "Evening Routine",
  Custom: "Custom Routine",
  Mood: "Mood Tracker",
  Journey: "Journeys",
};

export const gendersMap = {
  male: "male",
  female: "female",
  nonBinary: "nonBinary",
  secret: "nonBinary",
  other: "nonBinary",
  caseIndependent: "caseIndependent",
};
